<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import FormInput from '../../components/Form/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PasswordRequirements from '../auth/signup/components/Signup/PasswordRequirements.vue';
import Testimonials from '../auth/signup/components/Testimonials/Index.vue';
import { isValidPassword } from 'shared/helpers/Validators';

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const bootstrap = window.shopifyCompleteSignup || {};
const credentials = reactive({
  email: bootstrap.email || '',
  password: '',
  confirmPassword: '',
});
const isSubmitting = ref(false);
const formRef = ref(null);
const skipFormRef = ref(null);

const rules = computed(() => ({
  credentials: {
    email: { required, email },
    password: {
      required,
      isValidPassword,
      minLength: minLength(6),
    },
    confirmPassword: {
      required,
      minLength: minLength(6),
      isEqPassword(value) {
        return value === credentials.password;
      },
    },
  },
}));

const v$ = useVuelidate(rules, { credentials });

const shopName = computed(() => bootstrap.shopName || '');
const csrfToken = computed(
  () => document.querySelector('meta[name="csrf-token"]')?.content || ''
);

const passwordErrorText = computed(() => {
  const password = v$.value.credentials.password;
  if (!password.$error) return '';
  if (password.minLength.$invalid)
    return t('SHOPIFY_COMPLETE_SIGNUP.PASSWORD.ERROR');
  if (password.isValidPassword.$invalid) {
    return t('SHOPIFY_COMPLETE_SIGNUP.PASSWORD.IS_INVALID_PASSWORD');
  }
  return '';
});

onMounted(() => {
  document.body.classList.remove('dark');
  document.documentElement.style.setProperty('color-scheme', 'light');
  (bootstrap.errors || []).forEach(message => useAlert(message));
});

const submit = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  formRef.value?.submit();
};

const skip = () => {
  skipFormRef.value?.submit();
};
</script>

<template>
  <div class="w-full h-full min-h-screen bg-gray-50">
    <div class="flex h-full min-h-screen flex-col lg:flex-row">
      <div
        class="flex-1 lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 lg:py-12 bg-white"
      >
        <div class="w-full max-w-md space-y-8">
          <div class="text-left">
            <img
              :src="globalConfig.logo"
              :alt="globalConfig.installationName"
              class="h-8 w-auto"
            />
            <h1
              class="mt-6 text-2xl font-medium tracking-tight text-n-slate-12"
            >
              {{ $t('SHOPIFY_COMPLETE_SIGNUP.HEADING') }}
            </h1>
            <p class="mt-2 text-sm text-gray-600">
              {{ $t('SHOPIFY_COMPLETE_SIGNUP.BODY', { shopName }) }}
            </p>
            <p class="mt-4 text-sm font-medium text-gray-800">
              {{ $t('SHOPIFY_COMPLETE_SIGNUP.SUBHEADING') }}
            </p>
          </div>

          <form
            ref="formRef"
            class="space-y-6"
            method="post"
            action="/shopify/complete_signup"
            @submit.prevent="submit"
          >
            <input type="hidden" name="authenticity_token" :value="csrfToken" />
            <FormInput
              v-model="credentials.email"
              type="email"
              name="email"
              class="w-full"
              :label="$t('SHOPIFY_COMPLETE_SIGNUP.EMAIL.LABEL')"
              :placeholder="$t('SHOPIFY_COMPLETE_SIGNUP.EMAIL.PLACEHOLDER')"
              :has-error="v$.credentials.email.$error"
              :error-message="$t('SHOPIFY_COMPLETE_SIGNUP.EMAIL.ERROR')"
              @blur="v$.credentials.email.$touch"
            />
            <div class="relative">
              <FormInput
                v-model="credentials.password"
                type="password"
                name="password"
                class="w-full"
                :label="$t('SHOPIFY_COMPLETE_SIGNUP.PASSWORD.LABEL')"
                :placeholder="
                  $t('SHOPIFY_COMPLETE_SIGNUP.PASSWORD.PLACEHOLDER')
                "
                :has-error="v$.credentials.password.$error"
                :error-message="passwordErrorText"
                @blur="v$.credentials.password.$touch"
              />
              <PasswordRequirements
                v-if="credentials.password"
                :password="credentials.password"
              />
            </div>
            <FormInput
              v-model="credentials.confirmPassword"
              type="password"
              name="password_confirmation"
              class="w-full"
              :label="$t('SHOPIFY_COMPLETE_SIGNUP.CONFIRM_PASSWORD.LABEL')"
              :placeholder="
                $t('SHOPIFY_COMPLETE_SIGNUP.CONFIRM_PASSWORD.PLACEHOLDER')
              "
              :has-error="v$.credentials.confirmPassword.$error"
              :error-message="
                $t('SHOPIFY_COMPLETE_SIGNUP.CONFIRM_PASSWORD.ERROR')
              "
              @blur="v$.credentials.confirmPassword.$touch"
            />
            <NextButton
              lg
              type="submit"
              class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 shadow-sm"
              icon="i-lucide-chevron-right"
              trailing-icon
              :label="$t('SHOPIFY_COMPLETE_SIGNUP.SUBMIT')"
              :disabled="isSubmitting"
              :is-loading="isSubmitting"
            />
          </form>

          <form
            ref="skipFormRef"
            method="post"
            action="/shopify/complete_signup"
          >
            <input type="hidden" name="authenticity_token" :value="csrfToken" />
            <input type="hidden" name="skip" value="1" />
            <button
              type="button"
              class="text-sm text-blue-600 hover:text-blue-500 underline"
              @click="skip"
            >
              {{ $t('SHOPIFY_COMPLETE_SIGNUP.SKIP') }}
            </button>
          </form>
        </div>
      </div>

      <div
        class="hidden lg:flex lg:w-1/3 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden"
      >
        <div
          class="relative z-10 flex flex-col justify-center px-8 xl:px-12 py-12 text-white bg-n-brand"
        >
          <Testimonials />
        </div>
      </div>
    </div>
  </div>
</template>
